import type { JSONContent } from '@tiptap/core';
import { type CommandProps, Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { diffWords } from 'diff';

import { getDocument } from '@/lib/utils';

const richTextDiffPluginKey = new PluginKey<PluginState>('richTextDiff');

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    richTextDiff: {
      setComparisonContent: (content: JSONContent | null) => ReturnType;
    };
  }
}

type PluginState = {
  comparisonDoc: ProseMirrorNode | null;
  decorations: DecorationSet;
};

type BlockEntry = {
  node: ProseMirrorNode;
  pos: number;
};

type BlockAnchor = {
  oldIndex: number;
  newIndex: number;
};

function textOf(node: ProseMirrorNode): string {
  if (node.isText) {
    return node.text ?? '';
  }

  let text = '';
  node.forEach((child) => {
    text += textOf(child);
  });
  return text;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function topLevelBlocks(doc: ProseMirrorNode): BlockEntry[] {
  const blocks: BlockEntry[] = [];
  doc.forEach((node, offset) => {
    blocks.push({ node, pos: offset });
  });
  return blocks;
}

// Maps each text node inside a block to its ProseMirror document positions.
// resolveOffset() uses this to translate a character offset within the block's
// plain text into the exact document position needed by Decoration.inline/widget.
function textSpans(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): Array<{ start: number; end: number }> {
  const spans: Array<{ start: number; end: number }> = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) {
      return;
    }

    spans.push({ start: pos, end: pos + node.nodeSize });
  });
  return spans;
}

// Converts a character offset within a block's flattened plain text into the
// corresponding ProseMirror document position, walking the text spans in order.
function resolveOffset(
  spans: Array<{ start: number; end: number }>,
  offset: number,
): number | null {
  if (spans.length === 0) {
    return null;
  }

  let consumed = 0;

  for (const span of spans) {
    const length = span.end - span.start;
    if (offset < consumed + length) {
      return span.start + (offset - consumed);
    }
    consumed += length;
  }

  return spans[spans.length - 1]?.end ?? null;
}

function blockSignature(block: BlockEntry): string {
  return `${block.node.type.name}|${normalizeText(textOf(block.node))}`;
}

// LCS over block signatures: finds paragraphs/headings that are identical between
// old and new so they can be used as stable anchors. Only blocks between anchors
// are diffed — inserting a new paragraph won't cause every subsequent block to
// appear modified.
function findAnchors(oldBlocks: BlockEntry[], newBlocks: BlockEntry[]): BlockAnchor[] {
  const oldSignatures = oldBlocks.map(blockSignature);
  const newSignatures = newBlocks.map(blockSignature);
  const dp = Array.from({ length: oldBlocks.length + 1 }, () =>
    Array<number>(newBlocks.length + 1).fill(0),
  );

  for (let oldIndex = oldBlocks.length - 1; oldIndex >= 0; oldIndex -= 1) {
    for (let newIndex = newBlocks.length - 1; newIndex >= 0; newIndex -= 1) {
      if (oldSignatures[oldIndex] === newSignatures[newIndex]) {
        dp[oldIndex][newIndex] = dp[oldIndex + 1][newIndex + 1] + 1;
      } else {
        dp[oldIndex][newIndex] = Math.max(dp[oldIndex + 1][newIndex], dp[oldIndex][newIndex + 1]);
      }
    }
  }

  const anchors: BlockAnchor[] = [];
  let oldIndex = 0;
  let newIndex = 0;

  while (oldIndex < oldBlocks.length && newIndex < newBlocks.length) {
    if (oldSignatures[oldIndex] === newSignatures[newIndex]) {
      anchors.push({ oldIndex, newIndex });
      oldIndex += 1;
      newIndex += 1;
      continue;
    }

    if (dp[oldIndex + 1][newIndex] >= dp[oldIndex][newIndex + 1]) {
      oldIndex += 1;
    } else {
      newIndex += 1;
    }
  }

  return anchors;
}

function createRemovedWidget(text: string): HTMLElement | null {
  const doc = getDocument();
  if (!doc) return null;
  const span = doc.createElement('span');
  span.textContent = text;
  span.className = 'text-inherit line-through opacity-80';
  span.style.backgroundColor = 'var(--diff-removed)';
  span.style.whiteSpace = 'pre-wrap';
  return span;
}

function addInlineDecoration(
  decorations: Decoration[],
  spans: Array<{ start: number; end: number }>,
  fromOffset: number,
  toOffset: number,
): void {
  const from = resolveOffset(spans, fromOffset);
  const to = resolveOffset(spans, toOffset);

  if (from === null || to === null || from >= to) {
    return;
  }

  decorations.push(
    Decoration.inline(from, to, {
      class: 'text-inherit',
      style: 'background-color: var(--diff-added); color: inherit',
    }),
  );
}

function addWidgetDecoration(
  decorations: Decoration[],
  spans: Array<{ start: number; end: number }>,
  offset: number,
  text: string,
): void {
  if (!text) {
    return;
  }

  const pos = resolveOffset(spans, offset);
  if (pos === null) {
    return;
  }

  const el = createRemovedWidget(text);
  if (!el) return;

  decorations.push(Decoration.widget(pos, () => el, { side: -1 }));
}

// Produces decorations for one old→new block pair. Removed words are inserted
// as widget decorations (DOM nodes injected before the cursor position) so they
// appear inline without affecting the underlying document. Added words get an
// inline decoration (green background) over the real text nodes.
function buildBlockDecorations(
  doc: ProseMirrorNode,
  oldBlock: ProseMirrorNode | null,
  newBlock: BlockEntry,
): Decoration[] {
  const decorations: Decoration[] = [];
  const spans = textSpans(doc, newBlock.pos, newBlock.pos + newBlock.node.nodeSize);
  const newText = textOf(newBlock.node);

  if (spans.length === 0 || !newText) {
    return decorations;
  }

  if (!oldBlock || oldBlock.type.name !== newBlock.node.type.name) {
    if (oldBlock) {
      addWidgetDecoration(decorations, spans, 0, textOf(oldBlock));
    }
    addInlineDecoration(decorations, spans, 0, newText.length);
    return decorations;
  }

  const oldText = textOf(oldBlock);
  if (!oldText) {
    addInlineDecoration(decorations, spans, 0, newText.length);
    return decorations;
  }

  let newOffset = 0;

  for (const part of diffWords(oldText, newText)) {
    if (part.removed) {
      addWidgetDecoration(decorations, spans, newOffset, part.value);
      continue;
    }

    const nextOffset = newOffset + part.value.length;
    if (part.added) {
      addInlineDecoration(decorations, spans, newOffset, nextOffset);
    }
    newOffset = nextOffset;
  }

  return decorations;
}

function computeDecorations(
  doc: ProseMirrorNode,
  comparisonDoc: ProseMirrorNode | null,
): DecorationSet {
  if (!comparisonDoc) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  const oldBlocks = topLevelBlocks(comparisonDoc);
  const newBlocks = topLevelBlocks(doc);
  const anchors = findAnchors(oldBlocks, newBlocks);
  let oldCursor = 0;
  let newCursor = 0;

  const decorateGap = (oldEnd: number, newEnd: number) => {
    const oldGap = oldBlocks.slice(oldCursor, oldEnd);
    const newGap = newBlocks.slice(newCursor, newEnd);
    const pairCount = Math.min(oldGap.length, newGap.length);

    // Diff only the unmatched ranges between stable anchors. Extra new blocks in
    // the gap are treated as pure insertions instead of shifting the rest.
    for (let index = 0; index < pairCount; index += 1) {
      const newBlock = newGap[index];
      if (!newBlock) continue;

      decorations.push(...buildBlockDecorations(doc, oldGap[index]?.node ?? null, newBlock));
    }

    for (let index = pairCount; index < newGap.length; index += 1) {
      const newBlock = newGap[index];
      if (!newBlock) continue;

      decorations.push(...buildBlockDecorations(doc, null, newBlock));
    }
  };

  for (const anchor of anchors) {
    decorateGap(anchor.oldIndex, anchor.newIndex);
    oldCursor = anchor.oldIndex + 1;
    newCursor = anchor.newIndex + 1;
  }

  decorateGap(oldBlocks.length, newBlocks.length);

  return DecorationSet.create(doc, decorations);
}

export const RichTextDiffExtension = Extension.create({
  name: 'richTextDiff',

  addCommands() {
    return {
      setComparisonContent:
        (content) =>
        ({ tr, editor }: CommandProps) => {
          const comparisonDoc = content ? editor.schema.nodeFromJSON(content) : null;
          tr.setMeta(richTextDiffPluginKey, comparisonDoc);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: richTextDiffPluginKey,
        state: {
          init: () => ({ comparisonDoc: null, decorations: DecorationSet.empty }),
          apply(tr, pluginState, _oldState, newState) {
            const meta = tr.getMeta(richTextDiffPluginKey);
            const comparisonDoc =
              meta === undefined ? pluginState.comparisonDoc : (meta as ProseMirrorNode | null);
            // Only recompute decorations when the comparison doc changed (new meta)
            // or the editor's own content changed. Skips diffWords on focus/selection txns.
            const decorations =
              meta !== undefined || tr.docChanged
                ? computeDecorations(newState.doc, comparisonDoc)
                : pluginState.decorations;
            return { comparisonDoc, decorations };
          },
        },
        props: {
          decorations: (state) =>
            richTextDiffPluginKey.getState(state)?.decorations ?? DecorationSet.empty,
        },
      }),
    ];
  },
});
